import { useEffect, useRef } from 'preact/hooks';
import {
  pickRoundTripDates,
  shouldAutoSelectLeg,
  toAvailabilityKey,
} from '../utils';

/**
 * Round-trip auto-select wiring for `UnifiedDatePicker`.
 *
 * Watches the per-leg `fetched` flags and availability maps. When polling
 * reaches a commit point, it picks dates, applies guard rules, and fires the
 * supplied callbacks. The memo resets when disabled so a fresh modal open can
 * auto-select again.
 *
 * @param {Object} params See the extraction plan for the full contract.
 */
const useRoundTripAutoSelect = ({
  enabled,
  departureAvailability,
  returnAvailability,
  fetched,
  today,
  maxDate,
  hasConcreteDeparture,
  hasConcreteReturn,
  startDate,
  endDate,
  onCommitDeparture,
  onCommitReturn,
}) => {
  const memoRef = useRef({ departure: null, return: null });
  const { dep: departureFetched, ret: returnFetched } = fetched;

  useEffect(() => {
    if (!enabled) return;
    if (hasConcreteDeparture && hasConcreteReturn) return;

    const currentStartKey = toAvailabilityKey(startDate);
    const manualSelectionInProgress = Boolean(
      currentStartKey && !endDate && currentStartKey !== memoRef.current.departure
    );
    if (manualSelectionInProgress) return;

    const picked = pickRoundTripDates({
      departureAvailability,
      returnAvailability,
      today,
      maxDate,
      fetched: { dep: departureFetched, ret: returnFetched },
    });

    const departureKey = toAvailabilityKey(picked.departure);
    if (shouldAutoSelectLeg({
      pickedDateKey: departureKey,
      hasConcreteDate: hasConcreteDeparture,
      lastAutoSelectedKey: memoRef.current.departure,
      manualSelectionInProgress: false,
    })) {
      memoRef.current.departure = departureKey;
      onCommitDeparture(picked.departure);
    }

    const returnKey = toAvailabilityKey(picked.return);
    if (shouldAutoSelectLeg({
      pickedDateKey: returnKey,
      hasConcreteDate: hasConcreteReturn,
      lastAutoSelectedKey: memoRef.current.return,
      manualSelectionInProgress: false,
    })) {
      memoRef.current.return = returnKey;
      onCommitReturn(picked.return);
    }
  }, [
    enabled,
    departureAvailability,
    returnAvailability,
    departureFetched,
    returnFetched,
    today,
    maxDate,
    hasConcreteDeparture,
    hasConcreteReturn,
    startDate,
    endDate,
    onCommitDeparture,
    onCommitReturn,
  ]);

  useEffect(() => {
    if (!enabled) {
      memoRef.current = { departure: null, return: null };
    }
  }, [enabled]);
};

export default useRoundTripAutoSelect;
